/**
 * React Query hooks for Chat with Cubes API integration.
 *
 * These hooks provide:
 * - Session management (create, list, delete)
 * - Message fetching and sending
 * - Automatic cache invalidation
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '@/lib/api-client-hooks';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { normalizeApiBaseUrl } from '@/lib/utils';
import type {
  AgentType,
  ChatSession,
  ChatSessionListResponse,
  ChatMessage,
  ChatMessageListResponse,
  ChatResponse,
  CubeStatus,
  FileUploadPreviewResponse,
  CommitUploadResponse,
} from '@/types/chat';

// API Base URL - use runtime config or fall back to env var
//
// IMPORTANT: Some deployments mistakenly set REACT_APP_API_BASE_URL to include "/api/backend".
// The chat hooks below already prefix endpoints with "/api/backend", so we normalize to avoid
// calling ".../api/backend/api/backend/...".
const API_BASE_URL = normalizeApiBaseUrl(getRuntimeConfig().apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '');

// Help catch misconfigured production builds: CRA bakes REACT_APP_* at build time.
// If API_BASE_URL is empty in production, REST calls will hit the current origin (the frontend host),
// which typically returns index.html (HTML) for unknown paths.
if (process.env.NODE_ENV === 'production' && !API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    '[chat] REACT_APP_API_BASE_URL is empty in a production build. ' +
      'Chat REST calls will go to the frontend origin (e.g. https://cortex.bid.pe/api/backend/...) and likely return HTML. ' +
      'Set REACT_APP_API_BASE_URL to the backend origin (e.g. https://sourcing.bidequity.ai) and redeploy.'
  );
}

// =============================================================================
// Query Keys
// =============================================================================

/**
 * Query key factory for chat-related queries.
 * Namespaced to prevent collisions and enable targeted invalidation.
 */
export const chatKeys = {
  all: ['chat'] as const,
  sessions: (tenantId: number) => [...chatKeys.all, 'sessions', tenantId] as const,
  session: (tenantId: number, sessionId: string) => [...chatKeys.all, 'session', tenantId, sessionId] as const,
  messages: (tenantId: number, sessionId: string) => [...chatKeys.all, 'messages', tenantId, sessionId] as const,
};

// =============================================================================
// Session Hooks
// =============================================================================

/**
 * Hook to fetch chat sessions for the current user.
 *
 * @param userId - User ID (cortex_user.id)
 * @param tenantId - Tenant ID (from useTenant() context)
 * @param limit - Maximum sessions to fetch (default: 20)
 * @param offset - Pagination offset (default: 0)
 */
export function useChatSessions(
  userId: number | null,
  tenantId: number | null,
  limit: number = 20,
  offset: number = 0,
  agentType?: AgentType,
  companyId?: number | null
) {
  const { fetchJSON } = useAuthenticatedFetch();

  return useQuery<ChatSessionListResponse>({
    queryKey: [...chatKeys.sessions(tenantId ?? 0), agentType ?? 'all', companyId ?? 'all'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('user_id', userId!.toString());
      params.append('tenant_id', tenantId!.toString());
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (agentType) {
        params.append('agent_type', agentType);
      }
      if (companyId) {
        params.append('company_id', companyId.toString());
      }

      return fetchJSON<ChatSessionListResponse>(`${API_BASE_URL}/api/backend/chat/sessions?${params}`);
    },
    enabled: userId !== null && tenantId !== null,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single chat session.
 *
 * @param userId - User ID (cortex_user.id)
 * @param tenantId - Tenant ID
 * @param sessionId - Session ID to fetch
 */
export function useChatSession(userId: number | null, tenantId: number | null, sessionId: string | null) {
  const { fetchJSON } = useAuthenticatedFetch();

  return useQuery<ChatSession>({
    queryKey: chatKeys.session(tenantId ?? 0, sessionId ?? ''),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('user_id', userId!.toString());
      params.append('tenant_id', tenantId!.toString());

      return fetchJSON<ChatSession>(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}?${params}`);
    },
    enabled: userId !== null && tenantId !== null && sessionId !== null,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to create a new chat session.
 *
 * On success, invalidates the sessions list to show the new session.
 */
export function useCreateChatSession() {
  const queryClient = useQueryClient();
  const { fetchJSON } = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      userId,
      tenantId,
      agentType,
      companyId,
      companyName,
    }: {
      userId: number;
      tenantId: number;
      agentType?: AgentType;
      companyId?: number;
      companyName?: string;
    }) => {
      return fetchJSON<ChatSession>(`${API_BASE_URL}/api/backend/chat/sessions`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          tenant_id: tenantId,
          agent_type: agentType,
          company_id: companyId,
          company_name: companyName,
        }),
      });
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions(tenantId),
      });
    },
  });
}

/**
 * Hook to delete a chat session.
 *
 * On success, invalidates the sessions list and removes cached messages.
 */
export function useDeleteChatSession() {
  const queryClient = useQueryClient();
  const { fetchJSON } = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({ userId, tenantId, sessionId }: { userId: number; tenantId: number; sessionId: string }) => {
      return fetchJSON(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tenant_id: tenantId,
        }),
      });
    },
    onSuccess: (_, { tenantId, sessionId }) => {
      // Remove session and messages from cache
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions(tenantId),
      });
      queryClient.removeQueries({
        queryKey: chatKeys.messages(tenantId, sessionId),
      });
    },
  });
}

// =============================================================================
// Message Hooks
// =============================================================================

/**
 * Hook to fetch messages for a chat session.
 *
 * @param userId - User ID (cortex_user.id)
 * @param tenantId - Tenant ID
 * @param sessionId - Session ID to fetch messages for
 */
export function useChatMessages(userId: number | null, tenantId: number | null, sessionId: string | null) {
  const { fetchJSON } = useAuthenticatedFetch();

  return useQuery<ChatMessageListResponse>({
    queryKey: chatKeys.messages(tenantId ?? 0, sessionId ?? ''),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('user_id', userId!.toString());
      params.append('tenant_id', tenantId!.toString());

      return fetchJSON<ChatMessageListResponse>(
        `${API_BASE_URL}/api/backend/chat/sessions/${sessionId}/messages?${params}`
      );
    },
    enabled: userId !== null && tenantId !== null && sessionId !== null,
    staleTime: 10000, // 10 seconds - messages change frequently
    retry: (failureCount, error) => {
      // Don't retry 404s — session was deleted
      if (error instanceof Error && error.message.includes('404')) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to send a message and get the AI response.
 *
 * This is the main interaction hook for the chat feature.
 * On success, it invalidates the messages query to show both
 * the user message and the AI response.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { fetchJSON } = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      userId,
      tenantId,
      sessionId,
      content,
      cubeStatus = 'published',
    }: {
      userId: number;
      tenantId: number;
      sessionId: string;
      content: string;
      cubeStatus?: CubeStatus;
    }) => {
      return fetchJSON<ChatResponse>(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tenant_id: tenantId,
          content,
          cube_status: cubeStatus,
        }),
      });
    },
    onSuccess: (_, { tenantId, sessionId }) => {
      // Refresh messages to show both user message and AI response
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(tenantId, sessionId),
      });
      // Also refresh sessions list (title may have been updated)
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions(tenantId),
      });
    },
  });
}

// =============================================================================
// Optimistic Update Helpers (Optional Enhancement)
// =============================================================================

/**
 * Helper to optimistically add a user message to the cache
 * while waiting for the AI response.
 *
 * Usage:
 * ```typescript
 * const sendMessage = useSendMessage();
 * const addOptimistic = useOptimisticMessage(tenantId, sessionId);
 *
 * const handleSend = async (content: string) => {
 *   addOptimistic(content);  // Show immediately
 *   await sendMessage.mutateAsync({ tenantId, sessionId, content });
 * };
 * ```
 */
export function useOptimisticMessage(tenantId: number | null, sessionId: string | null) {
  const queryClient = useQueryClient();

  return (content: string) => {
    if (!tenantId || !sessionId) return;

    const queryKey = chatKeys.messages(tenantId, sessionId);

    // Get current messages
    const current = queryClient.getQueryData<ChatMessageListResponse>(queryKey);

    if (current) {
      // Add optimistic user message
      const optimisticMessage: ChatMessage = {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content,
        tool_calls: [],
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ChatMessageListResponse>(queryKey, {
        ...current,
        messages: [...current.messages, optimisticMessage],
      });
    }
  };
}

// =============================================================================
// File Upload Hook (for AI Upload chat)
// =============================================================================

/**
 * Hook to upload an Excel file to an AI Upload chat session.
 *
 * Sends the file as multipart/form-data and returns the job_id for polling.
 */
/**
 * Hook to upload an Excel file for AI-powered preview/analysis (synchronous).
 *
 * Returns the AI analysis of the file. The file is stored server-side
 * for later confirmation via useCommitUpload.
 */
export function useUploadFile() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      userId,
      tenantId,
      sessionId,
      file,
    }: {
      userId: number;
      tenantId: number;
      sessionId: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId.toString());

      const response = await fetchWithAuth(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${error}`);
      }

      return (await response.json()) as FileUploadPreviewResponse;
    },
    onSuccess: (_, { tenantId, sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(tenantId, sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions(tenantId),
      });
    },
  });
}

/**
 * Hook to confirm and trigger the full benchmarking pipeline for a
 * previously uploaded (previewed) Excel file.
 *
 * Returns a job_id for polling progress.
 */
export function useCommitUpload() {
  const queryClient = useQueryClient();
  const { fetchJSON } = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      userId,
      tenantId,
      sessionId,
      quickMode = false,
      userEmail,
    }: {
      userId: number;
      tenantId: number;
      sessionId: string;
      quickMode?: boolean;
      userEmail?: string;
    }) => {
      return fetchJSON<CommitUploadResponse>(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}/commit-upload`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          tenant_id: tenantId,
          quick_mode: quickMode,
          user_email: userEmail,
        }),
      });
    },
    onSuccess: (_, { tenantId, sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(tenantId, sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions(tenantId),
      });
    },
  });
}
